import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Trash2, Check, X, RefreshCw, Copy, Download, Send, Settings, Key, Clock, Zap, ExternalLink, Image as ImageIcon, Mic, Film, Play, Edit3, Share2, Facebook } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const KIND_META = {
  citation: { label: "Citation", color: "bg-amber-500/15 text-amber-300 border-amber-400/40" },
  proverbe: { label: "Proverbe", color: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" },
  actu:     { label: "Actu",     color: "bg-rose-500/15 text-rose-300 border-rose-400/40" },
  culture:  { label: "Culture",  color: "bg-violet-500/15 text-violet-300 border-violet-400/40" },
  history:  { label: "Histoire", color: "bg-cyan-500/15 text-cyan-300 border-cyan-400/40" },
};

const TONE_META = {
  fier:   { label: "Fier", emoji: "🇲🇬" },
  punchy: { label: "Punchy", emoji: "⚡" },
};

const STATUS_META = {
  draft:    { label: "Brouillon", color: "bg-white/5 text-white/70 border-white/10" },
  approved: { label: "Validé",    color: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" },
  rejected: { label: "Rejeté",    color: "bg-red-500/15 text-red-300 border-red-400/40" },
  posted:   { label: "Posté",     color: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/40" },
};

const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u || "");

export const AdminMadaWallsPanel = () => {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ draft: 0, approved: 0, rejected: 0, posted: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Generate form
  const [count, setCount] = useState(5);
  const [tone, setTone]   = useState("mix");
  const [model, setModel] = useState("gemini");
  const [withVoice, setWithVoice] = useState(false);
  const [allInOneBusy, setAllInOneBusy] = useState(false);
  const [allInOneStep, setAllInOneStep] = useState("");

  // Settings panel
  const [openSettings, setOpenSettings] = useState(false);
  const [settings, setSettings] = useState({ gemini_api_key: false, anthropic_api_key: false, auto_enabled: false, auto_count: 5, auto_hour: 8, cron_secret: "", cron_url: "" });
  const [geminiKeyInput, setGeminiKeyInput] = useState("");
  const [claudeKeyInput, setClaudeKeyInput] = useState("");

  useEffect(() => { loadPosts(); loadSettings(); }, [filter]);

  const loadPosts = () => {
    setLoading(true);
    api.get(`/admin/mada-walls/posts${filter === "all" ? "" : `?status=${filter}`}`)
      .then(({ data }) => { setPosts(data.posts || []); setStats(data.stats || {}); })
      .catch(() => toast.error("Impossible de charger les murs"))
      .finally(() => setLoading(false));
  };

  const loadSettings = useCallback(() => {
    api.get("/admin/mada-walls/settings")
      .then(({ data }) => setSettings({ ...data }))
      .catch(() => {});
  }, []);

  const saveSettings = useCallback(async () => {
    const elevenKey = (settings._elevenKey || "").trim();
    const payload = Object.assign(
      {
        auto_enabled: settings.auto_enabled,
        auto_count:   settings.auto_count,
        auto_hour:    settings.auto_hour,
        auto_voice:   !!settings.auto_voice,
        image_style:  settings.image_style,
        language_mode: settings.language_mode,
        decor_mode:    settings.decor_mode,
        show_watermark: !!settings.show_watermark,
        custom_handle: settings.custom_handle || "",
        elevenlabs_voice_id: settings.elevenlabs_voice_id,
        tts_default_lang:    settings.tts_default_lang,
      },
      geminiKeyInput.trim() ? { gemini_api_key: geminiKeyInput.trim() } : {},
      claudeKeyInput.trim() ? { anthropic_api_key: claudeKeyInput.trim() } : {},
      elevenKey ? { elevenlabs_api_key: elevenKey } : {},
    );
    try {
      await api.post("/admin/mada-walls/settings", payload);
      toast.success("Réglages enregistrés");
      setGeminiKeyInput(""); setClaudeKeyInput("");
      loadSettings();
    } catch (e) { toast.error("Échec sauvegarde"); }
  }, [settings.auto_enabled, settings.auto_count, settings.auto_hour, settings.auto_voice, settings.image_style, settings.language_mode, settings.decor_mode, settings.show_watermark, settings.custom_handle, settings.elevenlabs_voice_id, settings.tts_default_lang, settings._elevenKey, geminiKeyInput, claudeKeyInput, loadSettings]);

  // Sauvegarde instantanée d'un sous-ensemble de settings (pour les dropdowns inline)
  const onSettingsApply = useCallback(async (patch) => {
    setSettings(prev => ({ ...prev, ...patch }));
    try { await api.post("/admin/mada-walls/settings", patch); }
    catch { toast.error("Sauvegarde réglages échouée"); }
  }, []);

  // Bascule auto sur Claude si Gemini absent mais Anthropic présent
  useEffect(() => {
    if (settings.gemini_api_key === false && settings.anthropic_api_key === true && model === "gemini") {
      setModel("claude");
    }
  }, [settings.gemini_api_key, settings.anthropic_api_key, model]);

  const generate = async () => {
    // Bascule auto sur le moteur configuré
    let useModel = model;
    if (useModel === "gemini" && !settings.gemini_api_key && settings.anthropic_api_key) {
      useModel = "claude";
    }
    if (useModel === "claude" && !settings.anthropic_api_key && settings.gemini_api_key) {
      useModel = "gemini";
    }
    if (!settings.gemini_api_key && !settings.anthropic_api_key) {
      toast.error("Configure une clé Gemini OU Claude (⚙️ Réglages)");
      setOpenSettings(true);
      return;
    }
    if (useModel === "gemini" && !settings.gemini_api_key) {
      toast.error("Configure la clé Gemini (⚙️ Réglages)");
      setOpenSettings(true);
      return;
    }
    if (useModel === "claude" && !settings.anthropic_api_key) {
      toast.error("Configure la clé Anthropic (⚙️ Réglages)");
      setOpenSettings(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mada-walls/generate", { count, tone, model: useModel, with_voice: withVoice });
      const voiceMsg = data.voice_used ? ` 🎙️ ${data.produced - (data.voice_errors || 0)} voix off` : "";
      toast.success(`✨ ${data.produced} murs générés (${useModel}) !${voiceMsg}`);
      loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération échouée");
    } finally { setBusy(false); }
  };

  // ── Pipeline ALL-IN-ONE : génère N murs avec voix off, puis build vidéos en chaîne dans le navigateur
  const runAllInOne = async () => {
    const aiModel = settings.gemini_api_key ? "gemini" : (settings.anthropic_api_key ? "claude" : null);
    if (!aiModel)                       { toast.error("Configure Gemini ou Claude (⚙️ Réglages)"); setOpenSettings(true); return; }
    if (!settings.elevenlabs_api_key)   { toast.error("Clé ElevenLabs requise (⚙️ Réglages)"); setOpenSettings(true); return; }
    setAllInOneBusy(true);
    try {
      setAllInOneStep(`Génération IA (${count} murs) + voix off en chaîne...`);
      const { data } = await api.post("/admin/mada-walls/generate", { count, tone, model: aiModel, with_voice: true });
      const newPosts = (data.posts || []).filter(p => p.audio_url && p.image_vertical_url);
      toast.success(`✨ ${data.produced} murs + ${newPosts.length} voix générés`);

      // Boucle MP4 en navigateur
      for (let i = 0; i < newPosts.length; i++) {
        const p = newPosts[i];
        setAllInOneStep(`🎬 Vidéo ${i + 1} / ${newPosts.length}...`);
        try {
          await composeVideo(absUrl(p.image_vertical_url), absUrl(p.audio_url), `mur-${p.post_id}`);
        } catch (err) {
          toast.error(`Vidéo ${i + 1} échouée : ${err.message || ""}`);
        }
      }
      toast.success(`🚀 Pipeline terminé : ${newPosts.length} vidéos téléchargées !`);
      loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pipeline échoué");
    } finally {
      setAllInOneBusy(false); setAllInOneStep("");
    }
  };

  // ── "Build all videos" : pour des posts existants ayant déjà audio + image
  const buildAllExistingVideos = async () => {
    const candidates = posts.filter(p => p.audio_url && p.image_vertical_url);
    if (candidates.length === 0) { toast.error("Aucun mur prêt (génère d'abord la voix off)"); return; }
    setAllInOneBusy(true);
    try {
      for (let i = 0; i < candidates.length; i++) {
        setAllInOneStep(`🎬 ${i + 1} / ${candidates.length}...`);
        try {
          await composeVideo(absUrl(candidates[i].image_vertical_url), absUrl(candidates[i].audio_url), `mur-${candidates[i].post_id}`);
        } catch { /* skip */ }
      }
      toast.success(`✓ ${candidates.length} vidéos prêtes`);
    } finally { setAllInOneBusy(false); setAllInOneStep(""); }
  };

  // ── "Régen tout en photo" : reconstruit les visuels graffiti en mode photo Nano Banana
  const regenAllToPhoto = async () => {
    const graffitiPosts = posts.filter(p => {
      const url = p.image_vertical_url || "";
      return url && !url.includes("mur_v_") && !url.includes("mur_s_");
    });
    if (graffitiPosts.length === 0) { toast.info("Aucun mur graffiti à régénérer"); return; }
    if (!confirm(`Régénérer ${graffitiPosts.length} murs en photo réaliste ? (~25s par mur)`)) return;
    setAllInOneBusy(true);
    try {
      for (let i = 0; i < graffitiPosts.length; i++) {
        setAllInOneStep(`📷 Photo ${i + 1} / ${graffitiPosts.length}...`);
        try { await api.post(`/admin/mada-walls/${graffitiPosts[i].post_id}/regen`); }
        catch { /* skip on error, continue batch */ }
      }
      toast.success(`✓ ${graffitiPosts.length} murs reconstruits en photo`);
      loadPosts();
    } finally { setAllInOneBusy(false); setAllInOneStep(""); }
  };

  const updatePost = async (id, action) => {
    try {
      await api.post(`/admin/mada-walls/${id}/${action}`);
      if (action === "delete") toast.success("Supprimé");
      else if (action === "approve") toast.success("Validé ✓");
      else if (action === "reject") toast.success("Rejeté");
      else if (action === "posted") toast.success("Marqué comme posté 📤");
      loadPosts();
    } catch { toast.error("Action échouée"); }
  };

  const regenImages = async (id) => {
    try {
      await api.post(`/admin/mada-walls/${id}/regen`);
      toast.success("Visuels régénérés");
      loadPosts();
    } catch { toast.error("Régénération échouée"); }
  };

  const generateVoice = async (id, voiceId, lang) => {
    try {
      const { data } = await api.post(`/admin/mada-walls/${id}/voiceover`, { voice_id: voiceId, lang });
      toast.success(`🎙️ Voix off prête (${data.chars} car.)`);
      loadPosts();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voix off échouée");
      throw e;
    }
  };

  const copyCaption = (p) => {
    const txt = `${p.body_fr}\n\n${p.body_mg ? `(Malagasy) ${p.body_mg}\n\n` : ""}${p.hashtags || ""}\n\nvoamada.com`;
    navigator.clipboard.writeText(txt);
    toast.success("Caption copiée !");
  };

  return (
    <div className="space-y-6" data-testid="admin-mada-walls-panel">
      {/* Banner persistant si clés manquantes */}
      {(!settings.gemini_api_key && !settings.anthropic_api_key) && (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-400/40 p-4 flex items-start gap-3" data-testid="mada-walls-keys-banner">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <div className="font-bold text-amber-200 text-sm">Aucun moteur IA configuré</div>
            <p className="text-xs text-white/70 mt-1">
              Pour générer des murs, configure au moins une clé : <b className="text-orange-300">Gemini</b> (gratuit, recommandé — <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="underline">aistudio.google.com/apikey</a>) ou <b className="text-purple-300">Claude</b> (payant).
              {!settings.elevenlabs_api_key && <> Pour la voix off, ajoute aussi une clé <b className="text-cyan-300">ElevenLabs</b> (gratuit — <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer" className="underline">elevenlabs.io</a>).</>}
            </p>
            <button
              onClick={() => setOpenSettings(true)}
              data-testid="mada-walls-open-settings-banner"
              className="mt-2 px-3 py-1.5 rounded-lg bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 text-xs font-bold flex items-center gap-1.5"
            >
              <Settings size={12} /> Ouvrir les Réglages
            </button>
          </div>
        </div>
      )}
      {/* Banner Gemini absent (mais Claude présent) */}
      {!settings.gemini_api_key && settings.anthropic_api_key && (
        <div className="rounded-xl bg-cyan-500/5 border border-cyan-400/30 p-3 text-xs text-cyan-100/80 flex items-center gap-2" data-testid="mada-walls-claude-fallback">
          ℹ️ Gemini non configuré — la génération utilisera <b>Claude Sonnet</b> par défaut (clé Anthropic active).
        </div>
      )}

      {/* En-tête + actions */}
      <div className="rounded-2xl bg-gradient-to-br from-orange-500/10 via-rose-500/5 to-emerald-500/10 border border-white/10 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-black tracking-tight">
              🧱 Les Murs Parlent de Madagascar
            </h2>
            <p className="text-sm text-white/60 mt-2 max-w-2xl">
              Génère 3 à 15 visuels par jour (citations · proverbes · actu · culture) en français + malagasy,
              prêts à poster sur TikTok / Facebook. Style « fresque urbaine » automatisé.
            </p>
          </div>
          <button
            onClick={() => setOpenSettings(true)}
            data-testid="mada-walls-settings-btn"
            className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-2 text-sm"
          >
            <Settings size={16} /> Réglages
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mt-5">
          {Object.entries({
            draft: ["Brouillons", "bg-white/5"],
            approved: ["Validés", "bg-emerald-500/10"],
            rejected: ["Rejetés", "bg-red-500/10"],
            posted: ["Postés", "bg-fuchsia-500/10"],
          }).map(([k, [label, bg]]) => (
            <button
              key={k}
              onClick={() => setFilter(k === filter ? "all" : k)}
              className={`p-3 rounded-xl text-left transition-colors ${bg} border ${filter === k ? "border-white/30" : "border-white/10"} hover:border-white/20`}
              data-testid={`mada-walls-stat-${k}`}
            >
              <div className="text-2xl font-black">{stats[k] || 0}</div>
              <div className="text-xs text-white/60">{label}</div>
            </button>
          ))}
        </div>

        {/* Form génération */}
        <div className="grid md:grid-cols-5 gap-3 mt-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Nombre</label>
            <input
              type="number" min="3" max="15"
              value={count} onChange={e => setCount(Math.max(3, Math.min(15, parseInt(e.target.value) || 5)))}
              data-testid="mada-walls-count"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-orange-400/60"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Ton</label>
            <select
              value={tone} onChange={e => setTone(e.target.value)}
              data-testid="mada-walls-tone"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none"
            >
              <option value="mix">🇲🇬 Mix fier + punchy</option>
              <option value="fier">🇲🇬 Fier & culturel</option>
              <option value="punchy">⚡ Punchy & actu</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Style visuel</label>
            <select
              value={settings.image_style || "photo_ai"}
              onChange={e => onSettingsApply({ image_style: e.target.value })}
              data-testid="mada-walls-style"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none"
            >
              <option value="photo_ai">📷 Photo réelle (mur peint + rue) — Gemini</option>
              <option value="graffiti">🎨 Graffiti plat (GD, sans clé)</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Décor de fond</label>
            <select
              value={settings.decor_mode || "random"}
              onChange={e => onSettingsApply({ decor_mode: e.target.value })}
              data-testid="mada-walls-decor"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none"
            >
              <option value="random">🎲 Aléatoire (varie à chaque mur)</option>
              <option value="tana_analakely">🏙️ Tana — Analakely (marché)</option>
              <option value="tana_avenue">🏙️ Tana — Avenue de l'Indépendance</option>
              <option value="antsirabe_pousse">🎴 Antsirabe — Pousse-pousse colorés</option>
              <option value="nosy_be_beach">🏝️ Nosy Be — Plage Andilana</option>
              <option value="tulear_zebu">🌅 Tuléar — Charrette à zébu</option>
              <option value="ambositra_rice">🌾 Ambositra — Rizières en terrasses</option>
              <option value="diego_colbert">🎨 Diego — Rue Colbert colorée</option>
              <option value="toamasina_port">⛵ Toamasina — Port et cyclo-pousses</option>
              <option value="mahajanga_baobab">🌳 Mahajanga — Baobab et coucher de soleil</option>
              <option value="andasibe_forest">🌳 Andasibe — Forêt tropicale</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Langue</label>
            <select
              value={settings.language_mode || "fr"}
              onChange={e => onSettingsApply({ language_mode: e.target.value })}
              data-testid="mada-walls-lang"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none"
            >
              <option value="fr">🇫🇷 Français uniquement</option>
              <option value="bi">🇫🇷 + 🇲🇬 Bilingue</option>
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50">Moteur IA</label>
            <select
              value={model} onChange={e => setModel(e.target.value)}
              data-testid="mada-walls-model"
              className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none"
            >
              <option value="gemini">Gemini Flash (gratuit) ⭐</option>
              <option value="claude">Claude Sonnet (payant)</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={generate}
              disabled={busy}
              data-testid="mada-walls-generate-btn"
              className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-500/20"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
              {busy ? "Génération..." : "Générer le batch"}
            </button>
          </div>
        </div>

        {/* Toggle voix + Pipeline All-in-one */}
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
          <label className="flex items-center gap-2 text-xs cursor-pointer text-white/70 hover:text-white">
            <input
              type="checkbox" checked={withVoice}
              onChange={e => setWithVoice(e.target.checked)}
              data-testid="mada-walls-with-voice"
              className="rounded border-white/20"
            />
            <Mic size={12} className="text-cyan-300" />
            <span>Générer aussi la voix off (ElevenLabs)</span>
          </label>
          <div className="flex items-center gap-2">
            {posts.some(p => p.audio_url) && (
              <button
                onClick={buildAllExistingVideos}
                disabled={allInOneBusy}
                data-testid="mada-walls-build-all-videos"
                className="px-3 py-2 rounded-xl bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-200 text-xs flex items-center gap-2 disabled:opacity-50"
                title="Build vidéos pour tous les murs ayant déjà une voix off"
              >
                <Film size={14} /> Build all vidéos
              </button>
            )}
            {posts.some(p => (p.image_vertical_url || "").includes("mur_1080x")) && (
              <button
                onClick={regenAllToPhoto}
                disabled={allInOneBusy}
                data-testid="mada-walls-regen-all-photo"
                className="px-3 py-2 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 text-xs flex items-center gap-2 disabled:opacity-50"
                title="Reconstruit les murs graffiti en photo Nano Banana"
              >
                <ImageIcon size={14} /> Régen tout en photo
              </button>
            )}
            <button
              onClick={runAllInOne}
              disabled={allInOneBusy}
              data-testid="mada-walls-all-in-one"
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-fuchsia-500/20"
              title="Génère le batch + voix off + vidéos MP4 en chaîne"
            >
              {allInOneBusy ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
              {allInOneBusy ? (allInOneStep || "En cours...") : "🚀 All-in-one (Gen + Voice + Vidéos)"}
            </button>
          </div>
        </div>
      </div>

      {/* Liste posts */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 className="animate-spin mr-2" /> Chargement...
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-20 text-white/40 border border-dashed border-white/10 rounded-2xl">
          <div className="text-6xl mb-3">🧱</div>
          <p>Aucun mur pour le moment.</p>
          <p className="text-sm">Clique sur <b>Générer le batch</b> pour démarrer ✨</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="mada-walls-grid">
          {posts.map((p) => (
            <PostCard
              key={p.post_id}
              post={p}
              onApprove={() => updatePost(p.post_id, "approve")}
              onReject={() => updatePost(p.post_id, "reject")}
              onPosted={() => updatePost(p.post_id, "posted")}
              onDelete={() => { if (confirm("Supprimer définitivement ce mur ?")) updatePost(p.post_id, "delete"); }}
              onRegen={() => regenImages(p.post_id)}
              onCopy={() => copyCaption(p)}
              onVoice={(voiceId, lang) => generateVoice(p.post_id, voiceId, lang)}
              onEdit={async (patch) => {
                const { data } = await api.post(`/admin/mada-walls/${p.post_id}/edit`, patch);
                toast.success("Édition enregistrée");
                loadPosts();
                return data;
              }}
              settings={settings}
            />
          ))}
        </div>
      )}

      {/* Modal Réglages */}
      {openSettings && (
        <SettingsModal
          settings={settings}
          geminiKeyInput={geminiKeyInput}
          claudeKeyInput={claudeKeyInput}
          onClose={() => setOpenSettings(false)}
          onGeminiChange={setGeminiKeyInput}
          onClaudeChange={setClaudeKeyInput}
          onSettingsChange={setSettings}
          onSave={saveSettings}
        />
      )}
    </div>
  );
};

// ── PostCard ──────────────────────────────────────────────────
const PostCard = ({ post: p, onApprove, onReject, onPosted, onDelete, onRegen, onCopy, onVoice, onEdit, settings }) => {
  const km = KIND_META[p.kind] || KIND_META.citation;
  const sm = STATUS_META[p.status] || STATUS_META.draft;
  const tm = TONE_META[p.tone] || TONE_META.fier;
  const [tab, setTab] = useState("vertical");
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [voicePicker, setVoicePicker] = useState(false);
  const [editing, setEditing] = useState(false);

  const ttsConfigured = settings?.elevenlabs_api_key;
  const voices = settings?.tts_voices || {};
  const defaultVoice = settings?.elevenlabs_voice_id || "21m00Tcm4Tlm";
  const defaultLang  = settings?.tts_default_lang || "fr";

  const triggerVoice = async (voiceId, lang) => {
    if (!ttsConfigured) { toast.error("Configure ElevenLabs dans ⚙️ Réglages"); return; }
    setVoicePicker(false); setVoiceBusy(true);
    try { await onVoice(voiceId, lang); } finally { setVoiceBusy(false); }
  };

  const buildVideo = async () => {
    if (!p.audio_url || !p.image_vertical_url) {
      toast.error("Génère d'abord la voix off");
      return;
    }
    setVideoBusy(true);
    try {
      await composeVideo(absUrl(p.image_vertical_url), absUrl(p.audio_url), `mur-${p.post_id}`);
      toast.success("🎬 Vidéo téléchargée !");
    } catch (e) {
      toast.error("Échec build vidéo : " + (e.message || ""));
    } finally { setVideoBusy(false); }
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 hover:border-white/20 transition-colors" data-testid={`mada-wall-card-${p.post_id}`}>
      {/* Image preview */}
      <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black mb-3">
        <img
          src={absUrl(tab === "vertical" ? p.image_vertical_url : p.image_square_url)}
          alt={p.body_fr?.slice(0, 60)}
          className={tab === "vertical" ? "w-full h-full object-cover" : "w-full h-full object-contain"}
          loading="lazy"
        />
        <div className="absolute top-2 left-2 flex gap-1">
          <button onClick={() => setTab("vertical")} className={`px-2 py-1 rounded text-[10px] font-bold ${tab === "vertical" ? "bg-white text-black" : "bg-black/60 text-white/80"}`}>9:16</button>
          <button onClick={() => setTab("square")}   className={`px-2 py-1 rounded text-[10px] font-bold ${tab === "square"   ? "bg-white text-black" : "bg-black/60 text-white/80"}`}>1:1</button>
        </div>
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full border text-[10px] font-bold ${km.color}`}>{km.label}</div>
        {p.audio_url && (
          <div className="absolute bottom-2 left-2 right-2 bg-black/70 backdrop-blur rounded-lg px-2 py-1">
            <audio src={absUrl(p.audio_url)} controls preload="none" className="w-full h-7" data-testid={`audio-${p.post_id}`} />
          </div>
        )}
      </div>

      {/* Texte */}
      <p className="text-sm font-medium leading-relaxed text-white/90 line-clamp-3">{p.body_fr}</p>
      {p.body_mg && <p className="text-xs italic text-white/50 mt-1.5 line-clamp-2">— {p.body_mg}</p>}

      {/* Source */}
      {p.source_name && (
        <a href={p.source_url || "#"} target="_blank" rel="noreferrer" className="text-[11px] text-white/40 hover:text-white/70 mt-2 inline-flex items-center gap-1">
          <ExternalLink size={10} /> {p.source_name}
        </a>
      )}

      {/* Status + ton */}
      <div className="flex items-center gap-2 mt-3 text-[10px]">
        <span className={`px-2 py-0.5 rounded-full border ${sm.color}`}>{sm.label}</span>
        <span className="text-white/40">{tm.emoji} {tm.label}</span>
        {p.audio_url && <span className="text-cyan-300/80">🎙️ Voix off</span>}
      </div>

      {/* Voix off + Vidéo (ligne dédiée) */}
      <div className="grid grid-cols-2 gap-1.5 mt-2 relative">
        <button
          onClick={() => p.audio_url ? triggerVoice(p.audio_voice_id || defaultVoice, p.audio_lang || defaultLang) : setVoicePicker(v => !v)}
          disabled={voiceBusy}
          data-testid={`voice-${p.post_id}`}
          className="px-2.5 py-1.5 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-200 text-xs flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {voiceBusy ? <Loader2 className="animate-spin" size={12} /> : <Mic size={12} />}
          {p.audio_url ? "Régen voix" : "Voix off"}
        </button>
        <button
          onClick={buildVideo}
          disabled={videoBusy || !p.audio_url}
          data-testid={`video-${p.post_id}`}
          className="px-2.5 py-1.5 rounded-lg bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-200 text-xs flex items-center justify-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {videoBusy ? <Loader2 className="animate-spin" size={12} /> : <Film size={12} />}
          Vidéo TikTok
        </button>

        {/* Mini voice picker dropdown */}
        {voicePicker && (
          <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-zinc-900 border border-white/15 rounded-xl p-2 shadow-2xl shadow-cyan-500/10">
            <div className="text-[10px] uppercase tracking-wider text-white/40 px-1 mb-1">Choisis la voix & langue</div>
            <div className="flex gap-1 mb-2">
              {[["fr","🇫🇷 FR"],["mg","🇲🇬 MG"],["bi","FR+MG"]].map(([l, lbl]) => (
                <button key={l}
                  onClick={() => triggerVoice(defaultVoice, l)}
                  className="flex-1 px-2 py-1 rounded bg-white/5 hover:bg-cyan-500/20 text-[10px]"
                >{lbl}</button>
              ))}
            </div>
            <div className="max-h-40 overflow-auto space-y-0.5">
              {Object.entries(voices).map(([vid, v]) => (
                <button key={vid}
                  onClick={() => triggerVoice(vid, defaultLang)}
                  className={`w-full text-left px-2 py-1 rounded hover:bg-cyan-500/20 text-[10px] ${vid === defaultVoice ? "bg-cyan-500/10" : ""}`}
                >
                  <span className="font-bold">{v.gender === "F" ? "♀" : "♂"} {v.name}</span>
                  <span className="text-white/40 ml-1">— {v.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions principales */}
      <div className="grid grid-cols-2 gap-1.5 mt-2">
        <button onClick={onCopy} className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs flex items-center justify-center gap-1.5" data-testid={`copy-${p.post_id}`}>
          <Copy size={12} /> Caption
        </button>
        <a href={absUrl(p.zip_url)} download className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs flex items-center justify-center gap-1.5">
          <Download size={12} /> Pack ZIP
        </a>
        {p.status === "draft" && (
          <>
            <button onClick={onApprove} className="px-2.5 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs flex items-center justify-center gap-1.5" data-testid={`approve-${p.post_id}`}>
              <Check size={12} /> Valider
            </button>
            <button onClick={onReject} className="px-2.5 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs flex items-center justify-center gap-1.5">
              <X size={12} /> Rejeter
            </button>
          </>
        )}
        {p.status === "approved" && (
          <button onClick={onPosted} className="col-span-2 px-2.5 py-1.5 rounded-lg bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 text-xs flex items-center justify-center gap-1.5" data-testid={`posted-${p.post_id}`}>
            <Send size={12} /> Marquer comme posté
          </button>
        )}
        <button onClick={onRegen} className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs flex items-center justify-center gap-1.5">
          <RefreshCw size={12} /> Régen
        </button>
        <button onClick={() => setEditing(true)} data-testid={`edit-${p.post_id}`} className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs flex items-center justify-center gap-1.5">
          <Edit3 size={12} /> Éditer
        </button>
        <button onClick={onDelete} className="col-span-2 px-2.5 py-1.5 rounded-lg bg-red-500/5 hover:bg-red-500/15 text-red-400/80 text-xs flex items-center justify-center gap-1.5">
          <Trash2 size={12} /> Supprimer
        </button>
      </div>

      {/* Partage social */}
      <div className="grid grid-cols-2 gap-1.5 mt-2 pt-2 border-t border-white/5">
        <button
          onClick={() => shareToFacebook(p)}
          data-testid={`share-fb-${p.post_id}`}
          className="px-2.5 py-1.5 rounded-lg bg-blue-600/20 hover:bg-blue-600/40 text-blue-200 text-xs flex items-center justify-center gap-1.5"
          title="Partager sur Facebook (ouvre la fenêtre de partage)"
        >
          <Facebook size={12} /> Facebook
        </button>
        <button
          onClick={() => shareToTikTok(p)}
          data-testid={`share-tt-${p.post_id}`}
          className="px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-pink-500/20 to-cyan-500/20 hover:from-pink-500/40 hover:to-cyan-500/40 text-pink-200 text-xs flex items-center justify-center gap-1.5"
          title="Télécharge le visuel + ouvre TikTok upload"
        >
          <Share2 size={12} /> TikTok
        </button>
      </div>

      {editing && (
        <EditPostModal
          post={p}
          onClose={() => setEditing(false)}
          onSave={async (patch) => { await onEdit(patch); setEditing(false); }}
        />
      )}
    </div>
  );
};

// ── Edit modal ──────────────────────────────────────────────
const EditPostModal = ({ post, onClose, onSave }) => {
  const [body_fr, setBodyFr]         = useState(post.body_fr || "");
  const [body_mg, setBodyMg]         = useState(post.body_mg || "");
  const [kind, setKind]              = useState(post.kind || "citation");
  const [source_name, setSourceName] = useState(post.source_name || "");
  const [source_url, setSourceUrl]   = useState(post.source_url || "");
  const [hashtags, setHashtags]      = useState(post.hashtags || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try { await onSave({ body_fr, body_mg, kind, source_name, source_url, hashtags }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-zinc-900 border border-white/15 rounded-2xl p-6 max-w-xl w-full max-h-[90vh] overflow-auto" data-testid="mada-walls-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">✏️ Éditer le mur</h3>
          <button onClick={onClose}><X size={20} className="text-white/60 hover:text-white" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/50 block mb-1">Texte principal (français)</label>
            <textarea
              value={body_fr} onChange={e => setBodyFr(e.target.value)} maxLength={220} rows={3}
              data-testid="edit-body-fr"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-orange-400/60 resize-none"
            />
            <div className="text-[10px] text-white/40 text-right mt-0.5">{body_fr.length} / 220 — recommandé ≤ 180 pour lisibilité</div>
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Texte malagasy (optionnel)</label>
            <textarea
              value={body_mg} onChange={e => setBodyMg(e.target.value)} maxLength={220} rows={2}
              data-testid="edit-body-mg"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-orange-400/60 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 block mb-1">Type</label>
              <select value={kind} onChange={e => setKind(e.target.value)} data-testid="edit-kind"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
                <option value="citation">📜 Citation</option>
                <option value="proverbe">🌿 Proverbe</option>
                <option value="actu">📰 Actu</option>
                <option value="culture">🎨 Culture</option>
                <option value="history">🏛️ Histoire</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Source (optionnel)</label>
              <input value={source_name} onChange={e => setSourceName(e.target.value)} data-testid="edit-source"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">URL source (optionnel)</label>
            <input value={source_url} onChange={e => setSourceUrl(e.target.value)} placeholder="https://..."
              className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Hashtags</label>
            <input value={hashtags} onChange={e => setHashtags(e.target.value)} data-testid="edit-hashtags"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
          </div>
          <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3 text-xs text-amber-100/80">
            💡 Après sauvegarde, clique sur <b>Régen</b> pour reconstruire l'image avec le nouveau texte (Nano Banana, ~25s).
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm">Annuler</button>
          <button onClick={submit} disabled={busy} data-testid="edit-save"
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 disabled:opacity-50 font-bold text-sm flex items-center gap-2">
            {busy && <Loader2 className="animate-spin" size={14} />} Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Partage social ──────────────────────────────────────────
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

async function shareToFacebook(post) {
  const imgUrl = post.image_vertical_url ? `${BACKEND_URL}${post.image_vertical_url}` : "";
  const caption = `${post.body_fr}\n\n${post.hashtags || ""}`;
  // Tente Web Share API d'abord (mobile natif)
  if (navigator.share) {
    try {
      // Si le fichier est disponible côté backend, on télécharge et partage en blob
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const file = new File([blob], "mur-mada.png", { type: blob.type || "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: "Les Murs Parlent de Madagascar" });
        return;
      }
    } catch (e) { /* tombe sur fenêtre Facebook */ }
  }
  // Fallback : ouvre la fenêtre de partage Facebook avec l'URL du visuel
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(imgUrl)}&quote=${encodeURIComponent(caption)}`;
  window.open(fbUrl, "_blank", "width=600,height=700");
  try { await navigator.clipboard.writeText(caption); toast.success("Caption copiée — colle-la dans la fenêtre Facebook"); } catch {}
}

async function shareToTikTok(post) {
  const imgUrl = post.image_vertical_url ? `${BACKEND_URL}${post.image_vertical_url}` : "";
  const caption = `${post.body_fr}\n\n${post.hashtags || ""}`;

  toast.info("🎬 Préparation MP4 TikTok... (~6s)");
  let mp4Blob = null;
  try {
    if (post.audio_url) {
      // Si voix off existe, on génère le MP4 complet avec son
      mp4Blob = await composeVideoBlob(imgUrl, `${BACKEND_URL}${post.audio_url}`);
    } else {
      // Sinon, MP4 statique 5s avec léger zoom (TikTok n'accepte PAS les PNG)
      mp4Blob = await composeStaticVideoBlob(imgUrl, 5);
    }
  } catch (e) {
    toast.error("Conversion MP4 échouée : " + (e.message || ""));
    return;
  }

  const ext = mp4Blob.type.includes("mp4") ? "mp4" : "webm";
  const file = new File([mp4Blob], `mur-tiktok-${post.post_id}.${ext}`, { type: mp4Blob.type });

  // Web Share API natif (mobile)
  if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text: caption, title: "Les Murs Parlent de Madagascar" });
      toast.success("Partage natif TikTok ouvert 🚀");
      return;
    } catch { /* user annulé → fallback */ }
  }

  // Fallback desktop : télécharge le MP4 + copie caption + ouvre TikTok upload
  const a = document.createElement("a");
  a.href = URL.createObjectURL(mp4Blob);
  a.download = `mur-tiktok-${post.post_id}.${ext}`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  try { await navigator.clipboard.writeText(caption); } catch {}
  window.open("https://www.tiktok.com/upload", "_blank");
  toast.success(`MP4 (${ext}) téléchargé + caption copiée — TikTok ouvert`);
}

// MP4 statique : image fixe + zoom doux 6% sur N secondes (pour partage rapide TikTok sans voix off)
async function composeStaticVideoBlob(imageUrl, durationSec = 5) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Image inaccessible (CORS ?)"));
    i.src = imageUrl;
  });
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const stream = canvas.captureStream(25);
  const mime = MediaRecorder.isTypeSupported("video/mp4;codecs=avc1")
    ? "video/mp4;codecs=avc1"
    : MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm";
  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 3_500_000 });
  const chunks = [];
  recorder.ondataavailable = e => e.data.size && chunks.push(e.data);
  const stopped = new Promise(r => { recorder.onstop = r; });
  recorder.start();

  const t0 = performance.now();
  let raf;
  const draw = () => {
    const t = (performance.now() - t0) / 1000;
    const k = Math.min(1, t / durationSec);
    const zoom = 1 + 0.06 * k;
    const dW = W * zoom, dH = H * zoom;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, (W - dW) / 2, (H - dH) / 2, dW, dH);
    if (k < 1) raf = requestAnimationFrame(draw);
  };
  draw();
  await new Promise(r => setTimeout(r, durationSec * 1000 + 250));
  cancelAnimationFrame(raf);
  recorder.stop();
  await stopped;
  return new Blob(chunks, { type: mime });
}

// Compose vidéo image + audio (réutilise la logique existante mais retourne le Blob)
async function composeVideoBlob(imageUrl, audioUrl) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Image inaccessible"));
    i.src = imageUrl;
  });
  const audio = new Audio();
  audio.crossOrigin = "anonymous";
  audio.src = audioUrl;
  await new Promise((resolve, reject) => {
    audio.oncanplaythrough = resolve;
    audio.onerror = () => reject(new Error("Audio inaccessible"));
  });
  const duration = audio.duration || 5;
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const videoStream = canvas.captureStream(25);
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const dest = audioCtx.createMediaStreamDestination();
  const srcNode = audioCtx.createMediaElementSource(audio);
  srcNode.connect(dest);
  srcNode.connect(audioCtx.destination);
  const stream = new MediaStream([
    videoStream.getVideoTracks()[0],
    dest.stream.getAudioTracks()[0],
  ]);
  const mime = MediaRecorder.isTypeSupported("video/mp4;codecs=avc1,mp4a")
    ? "video/mp4;codecs=avc1,mp4a"
    : MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
  const chunks = [];
  recorder.ondataavailable = e => e.data.size && chunks.push(e.data);
  const stopped = new Promise(r => { recorder.onstop = r; });
  recorder.start();
  const t0 = performance.now();
  let raf;
  const draw = () => {
    const t = (performance.now() - t0) / 1000;
    const k = Math.min(1, t / duration);
    const zoom = 1 + 0.06 * k;
    const dW = W * zoom, dH = H * zoom;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, (W - dW) / 2, (H - dH) / 2, dW, dH);
    if (k < 1) raf = requestAnimationFrame(draw);
  };
  draw();
  audio.play();
  await new Promise(r => setTimeout(r, (duration + 0.3) * 1000));
  cancelAnimationFrame(raf);
  audio.pause();
  recorder.stop();
  await stopped;
  audioCtx.close();
  return new Blob(chunks, { type: mime });
}

// ── Vidéo composer (Canvas + MediaRecorder) ──────────────────
// Combine l'image verticale + audio MP3 en WebM/MP4 téléchargeable
async function composeVideo(imageUrl, audioUrl, filenameBase) {
  // 1. Charge l'image
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Image inaccessible (CORS ?)"));
    i.src = imageUrl;
  });

  // 2. Charge l'audio
  const audio = new Audio();
  audio.crossOrigin = "anonymous";
  audio.src = audioUrl;
  await new Promise((resolve, reject) => {
    audio.oncanplaythrough = resolve;
    audio.onerror = () => reject(new Error("Audio inaccessible"));
  });
  const duration = audio.duration || 5;

  // 3. Canvas 1080x1920
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // 4. Stream vidéo depuis le canvas + audio depuis le src
  const videoStream = canvas.captureStream(25);
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const dest = audioCtx.createMediaStreamDestination();
  const srcNode = audioCtx.createMediaElementSource(audio);
  srcNode.connect(dest);
  srcNode.connect(audioCtx.destination); // playback monitoring optionnel

  const stream = new MediaStream([
    videoStream.getVideoTracks()[0],
    dest.stream.getAudioTracks()[0],
  ]);

  const mime = MediaRecorder.isTypeSupported("video/mp4;codecs=avc1,mp4a")
    ? "video/mp4;codecs=avc1,mp4a"
    : MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
  const ext = mime.includes("mp4") ? "mp4" : "webm";

  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
  const chunks = [];
  recorder.ondataavailable = e => e.data.size && chunks.push(e.data);
  const stopped = new Promise(r => recorder.onstop = r);
  recorder.start();

  // Anim : zoom doux pendant la durée
  const t0 = performance.now();
  let raf;
  const draw = () => {
    const t = (performance.now() - t0) / 1000;
    const k = Math.min(1, t / duration);
    const zoom = 1 + 0.06 * k; // 6% zoom progressif
    const dW = W * zoom, dH = H * zoom;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, (W - dW) / 2, (H - dH) / 2, dW, dH);
    if (k < 1) raf = requestAnimationFrame(draw);
  };
  draw();
  audio.play();

  // Stop après duration + 0.3s buffer
  await new Promise(r => setTimeout(r, (duration + 0.3) * 1000));
  cancelAnimationFrame(raf);
  audio.pause();
  recorder.stop();
  await stopped;

  const blob = new Blob(chunks, { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${filenameBase}.${ext}`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  audioCtx.close();
}

// ── Modal Réglages ────────────────────────────────────────────
const SettingsModal = ({ settings, geminiKeyInput, claudeKeyInput, onClose, onGeminiChange, onClaudeChange, onSettingsChange, onSave }) => {
  const [elevenKeyInput, setElevenKeyInput] = useState("");
  const copyCron = () => {
    navigator.clipboard.writeText(settings.cron_url || "");
    toast.success("URL cron copiée");
  };
  const ttsStatus = settings.tts_status || {};
  const voices = settings.tts_voices || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-zinc-900 border border-white/15 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-auto" data-testid="mada-walls-settings-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">⚙️ Réglages — Murs Mada</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        {/* Gemini */}
        <section className="mb-6">
          <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Key size={14} /> Clé API Gemini (Google AI Studio — gratuit)</h4>
          <p className="text-xs text-white/50 mb-2">
            Récupère ta clé gratuite sur <a className="text-orange-300 underline" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">aistudio.google.com/apikey</a> (1500 req/jour offertes).
          </p>
          <input
            type="password"
            placeholder={settings.gemini_api_key ? "•••••••••••• (déjà configurée — saisir pour remplacer)" : "AIza..."}
            value={geminiKeyInput} onChange={e => onGeminiChange(e.target.value)}
            data-testid="settings-gemini-key"
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-orange-400/60"
          />
          {settings.gemini_api_key && <p className="text-xs text-emerald-400 mt-1">✓ Clé enregistrée</p>}
        </section>

        {/* Claude */}
        <section className="mb-6">
          <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Key size={14} /> Clé API Anthropic (Claude — payant, optionnel)</h4>
          <input
            type="password"
            placeholder={settings.anthropic_api_key ? "•••••••••••• (déjà configurée)" : "sk-ant-..."}
            value={claudeKeyInput} onChange={e => onClaudeChange(e.target.value)}
            data-testid="settings-claude-key"
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-orange-400/60"
          />
          {settings.anthropic_api_key && <p className="text-xs text-emerald-400 mt-1">✓ Clé enregistrée</p>}
        </section>

        {/* ElevenLabs */}
        <section className="mb-6 border-t border-white/10 pt-4">
          <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Mic size={14} className="text-cyan-300" /> Voix off ElevenLabs (10 000 car./mois gratuits)</h4>
          <p className="text-xs text-white/50 mb-2">
            Crée un compte gratuit sur <a className="text-cyan-300 underline" href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer">elevenlabs.io</a> et colle ta clé ici. Multilingual v2 prend en charge FR + MG.
          </p>
          <input
            type="password"
            placeholder={settings.elevenlabs_api_key ? "•••••••••••• (déjà configurée — saisir pour remplacer)" : "sk_..."}
            value={elevenKeyInput}
            onChange={e => { setElevenKeyInput(e.target.value); onSettingsChange({ ...settings, _elevenKey: e.target.value }); }}
            data-testid="settings-eleven-key"
            className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/60"
          />
          {settings.elevenlabs_api_key && (
            <div className="text-xs mt-1 flex items-center gap-3">
              <span className="text-emerald-400">✓ Clé enregistrée</span>
              {ttsStatus.ok && (
                <span className="text-white/50">Quota restant : <b className="text-cyan-300">{ttsStatus.remaining?.toLocaleString()} car.</b> / {ttsStatus.limit?.toLocaleString()} ({ttsStatus.tier})</span>
              )}
              {ttsStatus.ok === false && ttsStatus.error && <span className="text-red-400">⚠ {ttsStatus.error}</span>}
            </div>
          )}

          {/* Voix par défaut */}
          {settings.elevenlabs_api_key && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs text-white/50">Voix par défaut</label>
                <select
                  value={settings.elevenlabs_voice_id || "21m00Tcm4Tlm"}
                  onChange={e => onSettingsChange({ ...settings, elevenlabs_voice_id: e.target.value })}
                  data-testid="settings-eleven-voice"
                  className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm"
                >
                  {Object.entries(voices).map(([vid, v]) => (
                    <option key={vid} value={vid}>{v.gender === "F" ? "♀" : "♂"} {v.name} — {v.desc}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-white/50">Langue par défaut</label>
                <select
                  value={settings.tts_default_lang || "fr"}
                  onChange={e => onSettingsChange({ ...settings, tts_default_lang: e.target.value })}
                  data-testid="settings-eleven-lang"
                  className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm"
                >
                  <option value="fr">🇫🇷 Français uniquement</option>
                  <option value="mg">🇲🇬 Malagasy uniquement</option>
                  <option value="bi">FR + MG (bilingue)</option>
                </select>
              </div>
            </div>
          )}
        </section>

        {/* Branding / Watermark */}
        <section className="mb-6 border-t border-white/10 pt-4">
          <h4 className="font-bold text-sm flex items-center gap-2 mb-3">🏷️ Branding sur le visuel</h4>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox" checked={!!settings.show_watermark}
              onChange={e => onSettingsChange({ ...settings, show_watermark: e.target.checked })}
              data-testid="settings-show-watermark"
              className="rounded border-white/20"
            />
            <span>Afficher <code className="text-orange-300">voamada.com</code> + handle en bas du visuel</span>
          </label>
          <div className="mt-2 ml-6">
            <label className="text-xs text-white/50 block mb-1">Handle social personnalisé (remplace @voamada)</label>
            <input
              value={settings.custom_handle || ""}
              onChange={e => onSettingsChange({ ...settings, custom_handle: e.target.value })}
              placeholder="@voamada (défaut)"
              data-testid="settings-custom-handle"
              className="w-full max-w-xs rounded-xl bg-white/5 border border-white/10 px-3 py-1.5 text-sm focus:outline-none"
            />
            <p className="text-[10px] text-white/40 mt-1">Ex: <code>@siegfriedzanako</code> ou <code>@arnakclan</code> pour signer le mur</p>
          </div>
        </section>

        {/* Auto */}
        <section className="mb-6 border-t border-white/10 pt-4">
          <h4 className="font-bold text-sm flex items-center gap-2 mb-3"><Clock size={14} /> Génération automatique quotidienne</h4>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox" checked={settings.auto_enabled}
              onChange={e => onSettingsChange({ ...settings, auto_enabled: e.target.checked })}
              data-testid="settings-auto-enabled"
              className="rounded border-white/20"
            />
            <span className="text-sm">Activer le batch auto quotidien</span>
          </label>

          {settings.auto_enabled && (
            <label className="flex items-center gap-2 cursor-pointer mt-2 ml-6 text-cyan-200/80">
              <input
                type="checkbox" checked={!!settings.auto_voice}
                onChange={e => onSettingsChange({ ...settings, auto_voice: e.target.checked })}
                data-testid="settings-auto-voice"
                className="rounded border-white/20"
              />
              <Mic size={12} />
              <span className="text-xs">Inclure la voix off ElevenLabs dans chaque batch auto</span>
            </label>
          )}

          {settings.auto_enabled && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs text-white/50">Nombre par jour</label>
                <input
                  type="number" min="3" max="15"
                  value={settings.auto_count}
                  onChange={e => onSettingsChange({ ...settings, auto_count: parseInt(e.target.value) || 5 })}
                  className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-white/50">Heure (0-23, UTC)</label>
                <input
                  type="number" min="0" max="23"
                  value={settings.auto_hour}
                  onChange={e => onSettingsChange({ ...settings, auto_hour: parseInt(e.target.value) || 8 })}
                  className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          {/* URL cron */}
          {settings.auto_enabled && settings.cron_url && (
            <div className="mt-4 rounded-xl bg-amber-500/5 border border-amber-400/30 p-3 text-xs">
              <div className="font-bold text-amber-300 mb-1">⚙️ Configure ce cron sur OVH (1×/jour) :</div>
              <code className="block bg-black/40 rounded p-2 text-[10px] break-all">
                {`curl -s "${settings.cron_url}"`}
              </code>
              <button onClick={copyCron} className="mt-2 px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs flex items-center gap-1">
                <Copy size={11} /> Copier la commande
              </button>
              <p className="text-white/40 mt-2 leading-relaxed">
                {`Dans Manager OVH → Hébergement → Tâches planifiées : ajoute une tâche quotidienne à l'heure choisie qui lance ce curl.`}
              </p>
            </div>
          )}
        </section>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm">Annuler</button>
          <button onClick={onSave} data-testid="settings-save-btn" className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 font-bold text-sm flex items-center gap-2">
            <Zap size={14} /> Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};
